// Today-window computation for the Today button. Pure helper so we can
// unit-test the span-preservation + degenerate-span fallback + zoom-
// adaptive left anchor in isolation from vis-timeline.
//
// Preserves the caller's current viewport span (so a user who zoomed in
// to a week stays at one-week zoom) and offsets today from the left
// edge by `round((spanDays - 1) * 0.1)` whole days — 0 days at 1-day
// zoom (today fills the window), 1 day at 7-day zoom, 3 days at 30-day
// zoom, asymptoting to ~10% of span for larger windows. Sub-day zoom
// levels snap up to a one-day window so the button always lands on at
// least one full day. If the current span is non-finite or zero/
// negative — e.g. a fresh tab before the first range emits — we fall
// back to a one-month window.

const ONE_DAY_MS = 24 * 60 * 60 * 1000;
const FALLBACK_SPAN_MS = 31 * ONE_DAY_MS;
const LEFT_ANCHOR_FRACTION = 0.1;

export function computeTodayWindow(
  now: Date,
  currentViewport: { start: Date; end: Date },
): { start: Date; end: Date } {
  const rawSpan = currentViewport.end.getTime() - currentViewport.start.getTime();
  const validSpan = Number.isFinite(rawSpan) && rawSpan > 0 ? rawSpan : FALLBACK_SPAN_MS;
  // Snap sub-day zooms up to a full day so the button always shows the
  // whole day rather than a fragment.
  const span = Math.max(ONE_DAY_MS, validSpan);
  const spanDays = span / ONE_DAY_MS;
  const offsetDays = Math.max(0, Math.round((spanDays - 1) * LEFT_ANCHOR_FRACTION));
  const todayMid = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const start = new Date(todayMid - offsetDays * ONE_DAY_MS);
  const end = new Date(start.getTime() + span);
  return { start, end };
}

// Default initial span for a fresh tab (no c.start/c.end in the URL).
// Scales with viewport width so a phone gets a tighter view and an
// ultrawide doesn't waste pixels on horizontal whitespace:
//   < 900   px ->  7 days (compact / phone-shaped)
//   < 1600  px -> 31 days (desktop, current default)
//   < 2200  px -> 42 days (wide)
//   >= 2200 px -> 56 days (ultrawide)
// Non-finite / non-positive inputs fall back to the desktop span so
// SSR-ish bootstraps don't land on a degenerate window.
export function initialSpanDays(viewportWidthPx: number): number {
  if (!Number.isFinite(viewportWidthPx) || viewportWidthPx <= 0) return 31;
  if (viewportWidthPx < 900) return 7;
  if (viewportWidthPx < 1600) return 31;
  if (viewportWidthPx < 2200) return 42;
  return 56;
}

// Composes initialSpanDays + computeTodayWindow: derives the default
// span from the viewport width, then reuses the Today-button offset
// formula (round((spanDays - 1) * 0.1) days from the left edge) so the
// left anchor scales with the chosen span.
export function computeInitialViewport(
  now: Date,
  viewportWidthPx: number,
): { start: Date; end: Date } {
  const spanMs = initialSpanDays(viewportWidthPx) * ONE_DAY_MS;
  return computeTodayWindow(now, { start: new Date(0), end: new Date(spanMs) });
}
